/* eslint-disable react/display-name */
import { useLocation } from "@remix-run/react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";

import { IContentSection } from "./types";

import Typo from "~/components/atoms/Typo";
import { cn } from "~/utilities/cn";
import {
  SCROLL_OFFSET,
  SCROLL_OFFSET_MB,
} from "~/utilities/constants/projectSections";
import { ETypoVariant } from "~/utilities/enums/Typo";

export type Ref = HTMLDivElement;

export interface ContentsNavigatorProps {
  defaultSection: IContentSection;
  sections: IContentSection[];
  containerClass?: string;
  onClick: (index: number) => void;
}

const ContentsNavigator = forwardRef<Ref, ContentsNavigatorProps>(
  ({ defaultSection, sections, containerClass }, ref) => {
    const { t } = useTranslation();
    const location = useLocation();
    const isTabletOrMobile = useMediaQuery({ query: "(max-width: 992px)" });

    const [currentSection, setCurrentSection] = useState<string>(
      location.hash.replace("#", "") || defaultSection?.targetId
    );

    const scrollToView = useCallback(
      (targetId: string, offset: number = SCROLL_OFFSET) => {
        const sectionEl = document.getElementById(targetId);

        if (sectionEl) {
          const y =
            sectionEl.getBoundingClientRect().top + window.scrollY + offset;
          window.scrollTo({ top: y, behavior: "smooth" });

          // Update the URL hash without scrolling again
          window.history.pushState(null, "", `#${targetId}`);
        }

        setCurrentSection(targetId);
      },
      []
    );

    useEffect(() => {
      if (location.hash) {
        const timeoutId = setTimeout(() => {
          scrollToView(
            currentSection,
            isTabletOrMobile ? SCROLL_OFFSET_MB : SCROLL_OFFSET
          );
        }, 1000);

        return () => clearTimeout(timeoutId); // Cleanup to avoid memory leaks
      }
    }, [location.hash, currentSection, isTabletOrMobile, scrollToView]);

    return (
      <div
        ref={ref}
        className={cn(
          "sticky top-[70px] z-30 w-full overflow-hidden bg-backgroundPageProject lg:top-[47px] lg:p-0 lg:shadow-contents-navigator",
          containerClass
        )}
      >
        <ul className="scroll scroll-x box-border flex flex-row justify-center gap-x-6 overflow-x-auto border-b border-solid border-projectMenuBorderBottom bg-backgroundPageProject py-6 lg:gap-x-4.5 lg:px-6 lg:py-3">
          {sections.map(({ targetId, label, disabled }) => {
            const active = currentSection === targetId;

            return (
              <li
                key={targetId}
                onClick={() => {
                  if (!disabled) {
                    scrollToView(
                      targetId,
                      isTabletOrMobile ? SCROLL_OFFSET_MB : SCROLL_OFFSET
                    );
                  }
                }}
                aria-hidden
                className={cn(
                  "cursor-pointer",
                  disabled && "cursor-not-allowed"
                )}
              >
                <Typo
                  className={cn("whitespace-nowrap font-bold", {
                    "font-normal !text-color opacity-50": !active,
                  })}
                  tag="span"
                  variant={ETypoVariant.BODY_TITLE_16}
                >
                  {t(label)}
                </Typo>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
);

export default ContentsNavigator;
